import React, { useState, useRef, useEffect } from 'react';
import { Loader2, CheckCircle2, Circle, Brain, ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ThinkingStep } from '@/api/websocket';

// ThinkingStepItem component for displaying individual thinking steps
interface ThinkingStepItemProps {
  step: ThinkingStep;
  isComplete: boolean;
  isActive: boolean;
}

export function ThinkingStepItem({ step, isComplete, isActive }: ThinkingStepItemProps) {
  const getStepIcon = () => {
    if (isActive && !isComplete) {
      return <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />;
    }
    if (isComplete) {
      return <CheckCircle2 className="h-4 w-4 text-muted-foreground" />;
    }
    return <Circle className="h-4 w-4 text-muted-foreground/40" />;
  };

  return (
    <div
      className={cn(
        "flex items-start gap-3 py-2 px-3 rounded-lg border-l-2 border-l-border transition-all duration-300",
        "animate-in slide-in-from-left-2 fade-in duration-300",
        isActive && !isComplete ? "bg-muted/50" : "",
        isComplete ? "opacity-80" : ""
      )}
    >
      <div className="flex-shrink-0 mt-0.5">
        {getStepIcon()}
      </div>
      <div className={cn(
        "flex-1 text-sm leading-relaxed",
        isComplete ? "text-muted-foreground" : "text-foreground"
      )}>
        {step.text}
      </div>
    </div>
  );
}

// Component for displaying thinking data in completed messages
interface ThinkingDisplayProps {
  thinking: {
    text: string;
    steps: ThinkingStep[];
    isExpanded: boolean;
  };
  messageId: string;
  isStreaming?: boolean;
}

export function ThinkingDisplay({ thinking, messageId, isStreaming = false }: ThinkingDisplayProps) {
  // Default to expanded when streaming so users can see progress in real-time
  const [isExpanded, setIsExpanded] = useState(isStreaming || thinking.isExpanded);
  const stepsContainerRef = useRef<HTMLDivElement>(null);

  const steps = thinking.steps || [];
  const completedSteps = steps.length;

  // Auto-scroll to bottom when new steps are added during streaming
  useEffect(() => {
    if (isStreaming && isExpanded && stepsContainerRef.current) {
      stepsContainerRef.current.scrollTop = stepsContainerRef.current.scrollHeight;
    }
  }, [steps.length, isStreaming, isExpanded]);

  return (
    <div className="max-w-full">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "group flex items-center gap-3 px-4 py-3 bg-muted/50",
          "hover:bg-muted/80",
          "rounded-xl border border-border/50",
          "transition-all duration-300 text-sm font-medium",
          "focus:outline-none focus:ring-2 focus:ring-ring/30 focus:ring-offset-2 dark:focus:ring-offset-background"
        )}
      >
        <Brain className="h-4 w-4 text-muted-foreground group-hover:scale-110 transition-transform duration-300" />

        {isExpanded ? (
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3 w-3 text-muted-foreground" />
        )}

        <span className="text-foreground font-medium whitespace-nowrap">
          {isStreaming ? 'Thinking...' : `Thought about this`}
        </span>

        {completedSteps > 0 && (
          <Badge variant="outline" className="ml-2 text-xs whitespace-nowrap">
            {completedSteps} {completedSteps === 1 ? 'step' : 'steps'}
          </Badge>
        )}

        {isStreaming && (
          <div className="ml-auto flex items-center gap-1">
            <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-pulse"></div>
            <div className="w-1.5 h-1.5 bg-muted-foreground/70 rounded-full animate-pulse" style={{animationDelay: '0.15s'}}></div>
            <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-pulse" style={{animationDelay: '0.3s'}}></div>
          </div>
        )}
      </button>

      {isExpanded && steps.length > 0 && (
        <div
          ref={stepsContainerRef}
          className="mt-3 p-3 bg-muted/30 border border-border/50 rounded-xl max-h-64 overflow-y-auto space-y-2"
        >
          {steps.map((step, index) => (
            <ThinkingStepItem
              key={step.id || `step-${index}`}
              step={step}
              isComplete={!isStreaming || index < steps.length - 1}
              isActive={isStreaming && index === steps.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}
